import type { FlattenedSectionProps } from "../../../../../components/Questionnaire/Section";
import { LOCALE_EN_GB } from "../../../../../constants/constants";
import type { Locale } from "../../../../../types";
import { formatAnswer } from "../../../../questionnaire/utils/formatAnswer";
import type { PartialSection, QuestionnaireState } from "../../../types";
import { isShadowQuestion } from "../../../types";

interface MergeQuestionParams {
  question: FlattenedSectionProps["questions"][number];
  payloadQuestion: PartialSection["questions"][number] | undefined;
  answers: Record<string, unknown>;
  addressModes: Record<string, string>;
  locale: Locale;
  answerTimestamps: Record<string, number>;
  requestStartTime: number;
}

// Merge a single question's data from payload into state
function mergeQuestion({
  question,
  payloadQuestion,
  answers,
  addressModes,
  locale,
  answerTimestamps,
  requestStartTime,
}: MergeQuestionParams): FlattenedSectionProps["questions"][number] {
  const answer = payloadQuestion?.answer;
  let visible = payloadQuestion?.visible;

  // Override visibility for address_lookup fields
  if (addressModes[question.name]) {
    const addressMode = addressModes[question.name];
    visible = addressMode === "lookup" ? true : false;
  }

  // Override visibility for manual address fields
  if (question.associatedQuestionName) {
    const addressMode = addressModes[question.associatedQuestionName];
    visible = addressMode === "manual" ? true : false;
  }

  // Check if answer was modified locally after the request started
  const wasModifiedDuringRequest =
    (answerTimestamps[question.name] ?? 0) > requestStartTime;

  if (answer) {
    // Skip updating if answer was modified locally after request started
    if (wasModifiedDuringRequest) {
      // Keep existing local answer, but still merge other question properties
      return {
        ...question,
        ...payloadQuestion,
        answer: answers[question.name] as never,
        visible: visible ?? question.visible,
      };
    }

    // Handle data transformation for date type
    if (question.display?.type === "date") {
      answers[question.name] = formatAnswer(question, answer, locale) as string;
      return {
        ...question,
        ...payloadQuestion,
        answer: answers[question.name] as never,
      };
    }

    // Handle percentage split type
    if (question.display?.type === "percentage_split" && question.name) {
      const formattedAnswer = formatAnswer(question, answer, locale) as Record<
        string,
        string
      >;
      Object.assign(answers, formattedAnswer);
    } else {
      answers[question.name] = answer;
    }
  } else if (!isShadowQuestion(question) && !question.includeAnswer) {
    // Only delete if answer wasn't modified during request
    if (!wasModifiedDuringRequest) {
      // Delete answer if it doesn't exist in payload, unless it's a shadow question
      // or has includeAnswer explicitly set (e.g., boolean_group defaults)
      delete answers[question.name];
    }
  }

  return {
    ...question,
    ...payloadQuestion,
    visible: visible ?? question.visible,
  };
}

// Walk through each section in payload and merge into state.
// Questions are matched by their unique "name" key rather than position
// to handle cases where payload question order differs from state order.
// It assumes that response contains scalar values in question keys (not objects).
export const mergeState = (
  state: QuestionnaireState,
  payload: PartialSection[],
  locale: Locale = LOCALE_EN_GB,
  requestStartTime: number = 0,
): QuestionnaireState => {
  const {
    sections: stateSections,
    answers: stateAnswers,
    addressModes: stateAddressModes = {},
    answerTimestamps: stateAnswerTimestamps = {},
    ...rest
  } = state;

  const answers = { ...stateAnswers };
  const addressModes = { ...stateAddressModes };

  // Build lookup map from state sections by id
  // Exclude edit sections - they're artificial and shouldn't override real sections
  const stateSectionMap = new Map(
    (stateSections as unknown as FlattenedSectionProps[])
      .filter(s => s.displayAs !== "edit")
      .map(s => [s.id, s]),
  );

  // Build set of payload section ids for quick lookup
  const payloadSectionIds = new Set(payload.filter(s => s.id).map(s => s.id));

  // Preserve ReviewSection from state if not in payload
  // (API responses may not include it, but we need to keep it for navigation)
  const reviewSectionFromState = (
    stateSections as unknown as FlattenedSectionProps[]
  ).find(s => s.displayAs === "review" && !payloadSectionIds.has(s.id));

  // Iterate payload (authoritative order and existence)
  const sectionsFromPayload = payload.flatMap(
    (payloadSection): FlattenedSectionProps[] => {
      if (!payloadSection.id) {
        return [];
      }

      const stateSection = stateSectionMap.get(payloadSection.id);

      // New section - process questions through mergeQuestion for consistent behavior
      if (!stateSection) {
        const questions = (payloadSection.questions || []).map(q =>
          mergeQuestion({
            question: q as FlattenedSectionProps["questions"][number],
            payloadQuestion: q,
            answers,
            addressModes,
            locale,
            answerTimestamps: stateAnswerTimestamps,
            requestStartTime,
          }),
        );
        return [
          { ...payloadSection, questions } as unknown as FlattenedSectionProps,
        ];
      }

      const stateQuestionMap = new Map(
        stateSection.questions.map(q => [q.name, q]),
      );

      const payloadQuestionNames = new Set(
        (payloadSection.questions || []).map(q => q.name).filter(Boolean),
      );

      const questions = (payloadSection.questions || []).map(
        payloadQuestion => {
          const question = payloadQuestion.name
            ? stateQuestionMap.get(payloadQuestion.name)
            : undefined;

          return mergeQuestion({
            question: (question ??
              payloadQuestion) as FlattenedSectionProps["questions"][number],
            payloadQuestion,
            answers,
            addressModes,
            locale,
            answerTimestamps: stateAnswerTimestamps,
            requestStartTime,
          });
        },
      );

      // Clean up stale answers for state questions removed from the payload
      stateSection.questions
        .filter(q => {
          const wasRemovedFromPayload =
            q.name && !payloadQuestionNames.has(q.name);
          const ownsItsAnswer = !isShadowQuestion(q) && !q.includeAnswer;
          const wasUnchangedDuringRequest =
            (stateAnswerTimestamps[q.name] ?? 0) <= requestStartTime;

          return (
            wasRemovedFromPayload && ownsItsAnswer && wasUnchangedDuringRequest
          );
        })
        .forEach(q => delete answers[q.name]);

      return [{ ...stateSection, ...payloadSection, questions }];
    },
  ) as unknown as FlattenedSectionProps[];

  // Append ReviewSection if it was in state but not in payload
  const sections = reviewSectionFromState
    ? [...sectionsFromPayload, reviewSectionFromState]
    : sectionsFromPayload;

  return {
    sections,
    answers,
    addressModes,
    answerTimestamps: stateAnswerTimestamps,
    ...rest,
  };
};
